import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AudioLines, Check, FileSpreadsheet, FileText, Mic, Pencil, PhoneOff, Plus, Send, X } from 'lucide-react';
import { RealtimeFunctionCall, useOpenAiRealtime } from './aiAssistant/useOpenAiRealtime';
import { aiAssistantApi, AiAssistantStatus } from '../services/aiAssistantApi';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  askedAt?: string;
}

const TIPS = [
  'Who is working today?',
  'Whose visa is expiring?',
  'Show Maeve’s profile',
  'Is next week published?',
];

const WELCOME =
  'Ask about people, schedules, attendance, and timecards. Typed chat and voice both use live Staffly data. This assistant is read-only.';

function HistoryIcon({
  label,
  onClick,
  children,
  danger,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      className={`rounded-md p-1.5 ${danger ? 'text-red-600 hover:bg-red-50' : 'text-neutral-600 hover:bg-white'}`}
    >
      {children}
    </button>
  );
}

type BrowserSpeechRecognition = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: { results: ArrayLike<{ isFinal: boolean; 0?: { transcript: string } }> }) => void) | null;
  onerror: ((event?: { error?: string }) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
};

function chatAge(value: string) {
  const hasZone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(value);
  const then = new Date(hasZone ? value : `${value}Z`).getTime();
  if (Number.isNaN(then)) return '';
  const minutes = Math.floor(Math.max(0, Date.now() - then) / 60000);
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d`;
  if (days < 365) return `${Math.floor(days / 30)}mo`;
  return `${Math.floor(days / 365)}yr`;
}

function askedAtLabel(value?: string) {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function errorText(e: unknown, fallback: string) {
  const err = e as { response?: { data?: { message?: string } }; message?: string };
  return err.response?.data?.message || (e instanceof Error ? e.message : fallback);
}

const AiAssistant: React.FC = () => {
  const [draft, setDraft] = useState('');
  const [dictatePhase, setDictatePhase] = useState<'idle' | 'recording' | 'transcribing'>('idle');
  const [waveLevels, setWaveLevels] = useState<number[]>(() => Array.from({ length: 28 }, () => 0.18));
  const [messages, setMessages] = useState<ChatMessage[]>(() => [
    { id: 'welcome', role: 'assistant', text: WELCOME },
  ]);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState<Array<{ id: number; title: string; updatedAt: string }>>([]);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [draftTitle, setDraftTitle] = useState('');
  const [activeChatId, setActiveChatId] = useState<number | null>(null);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const chatIdRef = useRef<number | null>(null);
  const [status, setStatus] = useState<AiAssistantStatus | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [streaming, setStreaming] = useState('');
  const threadRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const recognitionRef = useRef<BrowserSpeechRecognition | null>(null);
  const dictateBaseRef = useRef('');
  const spokenRef = useRef('');
  const dictateEpoch = useRef(0);

  const openaiReady = !!status?.configured;
  const activeTitle = history.find((chat) => chat.id === activeChatId)?.title;

  const rememberChat = useCallback((id: number | null) => {
    chatIdRef.current = id;
    setActiveChatId(id);
  }, []);

  const rememberSavedChat = useCallback((id: number, title: string) => {
    chatIdRef.current = id;
    setActiveChatId(id);
    const updatedAt = new Date().toISOString();
    setHistory((rows) => {
      if (rows.some((row) => row.id === id)) {
        return rows.map((row) => (row.id === id ? { ...row, title: title || row.title, updatedAt } : row));
      }
      return [{ id, title: title || 'New chat', updatedAt }, ...rows];
    });
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const rows = await aiAssistantApi.chats();
      setHistory(rows);
    } catch {
      setHistory([]);
    }
  }, []);

  const appendUser = useCallback((text: string) => {
    setMessages((prev) => {
      if (prev[prev.length - 1]?.role === 'user' && prev[prev.length - 1]?.text === text) return prev;
      return [...prev, { id: `u-${Date.now()}`, role: 'user', text, askedAt: new Date().toISOString() }];
    });
  }, []);

  const appendAssistant = useCallback((text: string) => {
    setMessages((prev) => [...prev, { id: `a-${Date.now()}`, role: 'assistant', text }]);
  }, []);

  const handleRealtimeToolRef = useRef<(call: RealtimeFunctionCall) => void>(() => undefined);

  const realtime = useOpenAiRealtime({
    onUserTranscript: (text) => {
      appendUser(text);
      void aiAssistantApi.appendChat(chatIdRef.current, 'user', text).then((saved) => {
        rememberSavedChat(saved.chatId, saved.title);
        void loadHistory();
      }).catch(() => undefined);
    },
    onAssistantTranscript: (text, done) => {
      if (done) {
        setStreaming('');
        appendAssistant(text);
        void aiAssistantApi.appendChat(chatIdRef.current, 'assistant', text).then((saved) => {
          rememberSavedChat(saved.chatId, saved.title);
        }).catch(() => undefined);
      } else {
        setStreaming(text);
      }
    },
    onFunctionCall: (call) => handleRealtimeToolRef.current(call),
  });

  handleRealtimeToolRef.current = (call) => {
    void (async () => {
      try {
        const result = await aiAssistantApi.executeTool(call.name, call.arguments);
        realtime.sendFunctionOutput(call.callId, result);
      } catch (e) {
        const err = e as { response?: { data?: unknown }; message?: string };
        realtime.sendFunctionOutput(
          call.callId,
          err.response?.data ?? { error: err.message || 'Tool failed' },
        );
      }
    })();
  };

  useEffect(() => {
    let cancelled = false;
    aiAssistantApi
      .status()
      .then((s) => {
        if (!cancelled) setStatus(s);
      })
      .catch(() => {
        if (!cancelled) {
          setStatus({ configured: false, chatModel: '', realtimeModel: '' });
          setStatusError('Could not reach Ask Staffly.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    threadRef.current?.scrollTo({ top: threadRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, busy, streaming, realtime.micEnabled]);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  const saveTitle = async (id: number) => {
    const title = draftTitle.trim();
    if (!title) return;
    const saved = await aiAssistantApi.renameChat(id, title);
    setHistory((rows) => rows.map((row) => (row.id === id ? { ...row, title: saved.title } : row)));
    setEditingId(null);
  };

  const startNewChat = () => {
    rememberChat(null);
    setEditingId(null);
    setDownloadOpen(false);
    setMessages([{ id: 'welcome', role: 'assistant', text: WELCOME }]);
  };

  const openChat = async (id: number) => {
    const thread = await aiAssistantApi.chatThread(id);
    rememberChat(thread.id);
    setEditingId(null);
    setDownloadOpen(false);
    setMessages(thread.messages.map((m) => ({
      id: `saved-${m.id}`,
      role: m.role === 'user' ? 'user' : 'assistant',
      text: m.content,
      askedAt: m.role === 'user' ? m.createdAt : undefined,
    })));
  };

  const sendText = async (raw: string) => {
    const text = raw.trim();
    if (!text || busy || !openaiReady) return;
    setDraft('');
    appendUser(text);
    setBusy(true);
    try {
      const transcript = [...messages, { id: '', role: 'user' as const, text }]
        .filter((m) => m.id !== 'welcome')
        .map((m) => ({ role: m.role, content: m.text }));
      const result = await aiAssistantApi.chat(transcript, chatIdRef.current);
      rememberSavedChat(result.chatId, result.title);
      appendAssistant(result.text);
      void loadHistory();
    } catch (e) {
      appendAssistant(errorText(e, 'OpenAI chat failed.'));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (dictatePhase !== 'recording') return undefined;
    const timer = window.setInterval(() => {
      setWaveLevels((prev) => [...prev.slice(1), 0.15 + Math.random() * 0.85]);
    }, 120);
    return () => window.clearInterval(timer);
  }, [dictatePhase]);

  const applySpokenDraft = useCallback(() => {
    const spoken = spokenRef.current.trim();
    const base = dictateBaseRef.current.trim();
    if (spoken) setDraft(base ? `${base} ${spoken}` : spoken);
    spokenRef.current = '';
    setDictatePhase('idle');
  }, []);

  const stopDictation = useCallback(() => {
    dictateEpoch.current += 1;
    recognitionRef.current?.stop();
    recognitionRef.current = null;
    spokenRef.current = '';
    setDictatePhase('idle');
  }, []);

  const toggleDictation = () => {
    if (dictatePhase === 'transcribing') return;
    if (dictatePhase === 'recording') {
      setDictatePhase('transcribing');
      recognitionRef.current?.stop();
      return;
    }
    const speechWindow = window as Window & {
      SpeechRecognition?: new () => BrowserSpeechRecognition;
      webkitSpeechRecognition?: new () => BrowserSpeechRecognition;
    };
    const Ctor = speechWindow.SpeechRecognition || speechWindow.webkitSpeechRecognition;
    if (!Ctor) {
      setStatusError('This browser cannot dictate. Use the microphone in Chrome, or type the question.');
      return;
    }
    const recognition = new Ctor();
    dictateEpoch.current += 1;
    const epoch = dictateEpoch.current;
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    dictateBaseRef.current = draft;
    spokenRef.current = '';
    recognition.onresult = (event) => {
      let finalText = '';
      let interim = '';
      for (let i = 0; i < event.results.length; i += 1) {
        const piece = event.results[i][0]?.transcript ?? '';
        if (event.results[i].isFinal) finalText += piece;
        else interim += piece;
      }
      spokenRef.current = `${finalText}${interim}`.trim();
    };
    recognition.onerror = (event) => {
      if (epoch !== dictateEpoch.current) return;
      if (event?.error === 'aborted') return;
      recognitionRef.current = null;
      spokenRef.current = '';
      setDictatePhase('idle');
    };
    recognition.onend = () => {
      recognitionRef.current = null;
      window.setTimeout(() => {
        if (epoch !== dictateEpoch.current) return;
        applySpokenDraft();
      }, 280);
    };
    recognition.start();
    recognitionRef.current = recognition;
    setWaveLevels(Array.from({ length: 28 }, () => 0.18));
    setDictatePhase('recording');
  };

  const startVoice = async () => {
    if (!openaiReady) return;
    stopDictation();
    if (!realtime.connected) {
      const started = await realtime.connect();
      if (!started) return;
    }
    realtime.setMicEnabled(true);
  };

  useEffect(() => () => recognitionRef.current?.stop(), []);

  const onComposerKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void sendText(draft);
    }
  };

  const configMessage = statusError
    || (status && !status.configured
      ? 'OpenAI is not configured. Set OpenAI:ApiKey or OPENAI_API_KEY on Staffly.API.'
      : null);

  const savedChat = activeChatId != null;

  return (
    <div className="flex h-full min-h-0 overflow-hidden bg-white">
      <aside className="flex w-64 shrink-0 flex-col border-r border-neutral-200 bg-neutral-100">
        <div className="p-3">
          <button
            type="button"
            onClick={startNewChat}
            className="flex w-full items-center gap-2 rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm font-medium text-neutral-800 hover:bg-neutral-50"
          >
            <Plus className="h-4 w-4" />
            New
          </button>
        </div>
        <p className="px-4 pb-1 text-[11px] font-semibold uppercase tracking-wide text-neutral-500">Chats</p>
        <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
          {history.length === 0 && (
            <p className="px-2 py-3 text-xs text-neutral-500">No saved chats yet.</p>
          )}
          {history.map((chat) => {
            const selected = chat.id === activeChatId;
            return (
              <div
                key={chat.id}
                className={`group mb-0.5 flex items-center gap-1 rounded-lg px-2 py-2 ${
                  selected ? 'bg-white shadow-sm' : 'hover:bg-neutral-200/80'
                }`}
              >
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  {editingId === chat.id ? (
                    <input
                      value={draftTitle}
                      autoFocus
                      onChange={(event) => setDraftTitle(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') void saveTitle(chat.id);
                        if (event.key === 'Escape') setEditingId(null);
                      }}
                      className="w-full rounded border border-neutral-300 px-2 py-1 text-sm"
                    />
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => void openChat(chat.id)}
                        className="min-w-0 flex-1 truncate text-left text-sm text-neutral-800"
                        title={chat.title}
                      >
                        {chat.title}
                      </button>
                      <span className="shrink-0 text-[11px] text-neutral-400">{chatAge(chat.updatedAt)}</span>
                    </>
                  )}
                </div>
                <div className={`flex shrink-0 items-center ${editingId === chat.id ? '' : 'opacity-0 group-hover:opacity-100'}`}>
                  {editingId === chat.id ? (
                    <>
                      <HistoryIcon label="Save title" onClick={() => void saveTitle(chat.id)}>
                        <Check className="h-4 w-4" />
                      </HistoryIcon>
                      <HistoryIcon label="Cancel" onClick={() => setEditingId(null)}>
                        <X className="h-4 w-4" />
                      </HistoryIcon>
                    </>
                  ) : (
                    <HistoryIcon label="Rename" onClick={() => { setEditingId(chat.id); setDraftTitle(chat.title); }}>
                      <Pencil className="h-4 w-4" />
                    </HistoryIcon>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex-shrink-0 border-b border-neutral-200 px-4 py-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h1 className="truncate text-lg font-semibold text-neutral-900">{activeTitle || 'Ask Staffly'}</h1>
              {!openaiReady && (
                <p className="mt-0.5 text-xs text-neutral-500">
                  {status ? 'OpenAI is not configured' : 'Checking OpenAI…'}
                </p>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <div className="relative">
                <button
                  type="button"
                  disabled={!savedChat}
                  onClick={() => setDownloadOpen((open) => !open)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-neutral-200 px-2.5 py-1.5 text-xs font-medium text-neutral-700 hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Download
                </button>
                {downloadOpen && savedChat && (
                  <div className="absolute right-0 z-10 mt-1 w-40 rounded-md border border-neutral-200 bg-white py-1 shadow-lg">
                    <button
                      type="button"
                      onClick={() => {
                        setDownloadOpen(false);
                        if (activeChatId != null) void aiAssistantApi.downloadChat(activeChatId, 'xlsx');
                      }}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-neutral-700 hover:bg-neutral-50"
                    >
                      <FileSpreadsheet className="h-4 w-4" />
                      Excel
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setDownloadOpen(false);
                        if (activeChatId != null) void aiAssistantApi.downloadChat(activeChatId, 'pdf');
                      }}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-neutral-700 hover:bg-neutral-50"
                    >
                      <FileText className="h-4 w-4" />
                      PDF
                    </button>
                  </div>
                )}
              </div>
              {openaiReady && realtime.connected && (
                <button
                  type="button"
                  onClick={realtime.disconnect}
                  className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-medium text-red-800"
                >
                  <PhoneOff className="h-3.5 w-3.5" />
                  End voice
                </button>
              )}
            </div>
          </div>

          {configMessage && (
            <p className="mt-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900">{configMessage}</p>
          )}
          {realtime.error && (
            <p className="mt-2 rounded-md bg-red-50 px-3 py-2 text-xs text-red-800">{realtime.error}</p>
          )}
          {realtime.connecting && (
            <p className="mt-2 text-xs text-neutral-500">Starting OpenAI Realtime…</p>
          )}
        </header>

        <div ref={threadRef} className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-4">
          {messages.map((m) => (
            <div key={m.id} className={`flex flex-col ${m.role === 'user' ? 'items-end' : 'items-start'}`}>
              {m.role === 'user' && askedAtLabel(m.askedAt) && (
                <p className="mb-1 px-1 text-[11px] text-neutral-400">{askedAtLabel(m.askedAt)}</p>
              )}
              <div
                className={`max-w-[92%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed sm:max-w-[80%] ${
                  m.role === 'user'
                    ? 'rounded-br-md bg-blue-600 text-white'
                    : 'rounded-bl-md bg-neutral-100 text-neutral-800'
                }`}
              >
                <p className="whitespace-pre-wrap">{m.text}</p>
              </div>
            </div>
          ))}

          {(realtime.micEnabled || streaming) && (
            <div className="flex justify-start">
              <div className="max-w-[92%] rounded-2xl rounded-bl-md bg-blue-50 px-3.5 py-2.5 text-sm text-blue-900 sm:max-w-[80%]">
                <div className="mb-1 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-blue-700">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-400 opacity-75" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-blue-600" />
                  </span>
                  {streaming ? 'Speaking' : 'Listening'}
                </div>
                <p className="whitespace-pre-wrap">{streaming || 'Listening…'}</p>
              </div>
            </div>
          )}

          {busy && (
            <div className="flex justify-start">
              <div className="rounded-2xl rounded-bl-md bg-neutral-100 px-3.5 py-2.5 text-sm text-neutral-500">
                Thinking…
              </div>
            </div>
          )}
        </div>

        <div className="flex-shrink-0 border-t border-neutral-200 px-3 py-3">
          <div className="mb-2 flex flex-wrap gap-1.5">
            {TIPS.map((tip) => (
              <button
                key={tip}
                type="button"
                disabled={!openaiReady}
                onClick={() => {
                  setDraft(tip);
                  inputRef.current?.focus();
                }}
                className="rounded-full border border-neutral-200 bg-neutral-50 px-2.5 py-1 text-[11px] text-neutral-600 hover:bg-white disabled:opacity-50"
              >
                {tip}
              </button>
            ))}
          </div>

          <div className="flex items-end gap-2">
            <div className="flex min-h-12 flex-1 items-end rounded-full border border-neutral-300 bg-neutral-50 pl-4 pr-1 focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100">
              {dictatePhase === 'recording' ? (
                <div className="flex h-12 min-w-0 flex-1 items-center gap-0.5 overflow-hidden">
                  {waveLevels.map((level, index) => (
                    <span
                      key={index}
                      className="w-0.5 flex-shrink-0 rounded-full bg-neutral-900"
                      style={{ height: 4 + level * 20 }}
                    />
                  ))}
                </div>
              ) : dictatePhase === 'transcribing' ? (
                <p className="flex h-12 flex-1 items-center text-sm text-neutral-500">Transcribe</p>
              ) : (
                <textarea
                  ref={inputRef}
                  rows={1}
                  value={draft}
                  disabled={!openaiReady || busy}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={onComposerKeyDown}
                  placeholder={openaiReady ? 'Ask Staffly' : 'OpenAI is not configured'}
                  className="max-h-28 min-h-12 flex-1 resize-none bg-transparent py-3 text-sm outline-none disabled:text-neutral-400"
                />
              )}
              <button
                type="button"
                disabled={!openaiReady || busy || realtime.connecting || realtime.connected || dictatePhase === 'transcribing'}
                onClick={toggleDictation}
                className={`mb-1.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full disabled:opacity-40 ${
                  dictatePhase === 'recording' ? 'bg-neutral-900 text-white' : 'text-neutral-800 hover:bg-neutral-200'
                }`}
                aria-label={dictatePhase === 'recording' ? 'End dictation' : 'Dictate'}
                title={dictatePhase === 'recording' ? 'End dictation' : 'Dictate into the box'}
              >
                {dictatePhase === 'transcribing' ? (
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-neutral-400 border-t-transparent" />
                ) : dictatePhase === 'recording' ? (
                  <span className="h-3 w-3 rounded-sm bg-white" />
                ) : (
                  <Mic className="h-5 w-5" />
                )}
              </button>
            </div>

            {draft.trim() && !realtime.connected ? (
              <button
                type="button"
                disabled={!openaiReady || busy}
                onClick={() => void sendText(draft)}
                className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-blue-600 text-white hover:bg-blue-700 disabled:bg-neutral-300"
                aria-label="Send"
              >
                <Send className="h-5 w-5" />
              </button>
            ) : (
              <button
                type="button"
                disabled={!openaiReady || busy || realtime.connecting}
                onClick={() => {
                  if (realtime.connected) realtime.disconnect();
                  else void startVoice();
                }}
                className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full text-white disabled:bg-neutral-300 ${
                  realtime.connected ? 'bg-neutral-900 hover:bg-neutral-800' : 'bg-blue-600 hover:bg-blue-700'
                }`}
                aria-label={realtime.connected ? 'End voice' : 'Voice conversation'}
                title={realtime.connected ? 'End voice' : 'Voice conversation'}
              >
                {realtime.connecting ? (
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                ) : realtime.connected ? (
                  <PhoneOff className="h-5 w-5" />
                ) : (
                  <AudioLines className="h-5 w-5" />
                )}
              </button>
            )}
          </div>

          <p className="mt-2 text-[11px] text-neutral-400">
            The mic types what you say into the box. The voice button starts a spoken conversation. Both use live Staffly data.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AiAssistant;
